'use client';

import { useLayoutEffect, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { ImageOff, Image as ImageIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toDisplayMediaUrl } from '@/lib/media-url';
import { useMediaDelivery } from '@/hooks/useMediaDelivery';
import { useProgressiveMediaImage } from '@/hooks/useProgressiveMediaImage';

/** 缩略图铺满框且只读取已准备的小图；原图完整展示并逐级解码，复用同一交付缓存。 */
export function ShootingMedia({ src, assetId, variant = 'thumbnail', alt, className = '', style, onLoad }: {
  src?: string | null; assetId?: string | null; variant?: 'thumbnail' | 'original';
  alt: string; className?: string; style?: CSSProperties; onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
}) {
  const t = useTranslations('shooting');
  const delivery = useMediaDelivery(assetId, Boolean(src));
  const thumbnail = delivery.data?.image?.thumbnail?.url;
  const url = useProgressiveMediaImage({
    owner: `${assetId || ''}:${src || ''}:${variant}`,
    thumbnail,
    preview: variant === 'original' ? delivery.data?.image?.preview?.url : undefined,
    // 已准备的卡片不能继续自动下载原图；历史/外部素材仍使用授权原件。
    original: variant === 'original' || !thumbnail ? delivery.data?.original?.url || (src ? toDisplayMediaUrl(src) : undefined) : undefined,
    waitForDelivery: Boolean(assetId && delivery.loading),
  }) || undefined;
  const image = useRef<HTMLImageElement>(null);
  const [state, setState] = useState<{ url?: string; loaded: boolean; failed: boolean }>({ url, loaded: false, failed: false });
  useLayoutEffect(() => {
    const loaded = Boolean(url && image.current?.complete && image.current.naturalWidth);
    setState(previous => previous.url === url && previous.loaded === loaded && !previous.failed ? previous : { url, loaded, failed: false });
  }, [url]);
  const failed = state.url === url && state.failed;
  const loaded = state.url === url && state.loaded;
  return <span style={style} className={`relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden bg-slate-50 ${className}`}>
    {/* eslint-disable-next-line @next/next/no-img-element -- 批量鉴权后的交付 URL 保留稳定图片 DOM */}
    <img ref={image} src={url} alt={alt} decoding="async" className={`h-full w-full ${variant === 'original' ? 'object-contain' : 'object-cover'} ${loaded && !failed ? '' : 'invisible'}`}
      onLoad={event => { setState({ url, loaded: true, failed: false }); onLoad?.(event); }}
      onError={() => setState({ url, loaded: false, failed: true })} />
    {((!url && !delivery.loading) || failed) && <span role="img" aria-label={failed ? t('ui.imageUnavailable', { name: alt }) : alt} className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-500">{failed ? <ImageOff size={22} /> : <ImageIcon size={22} />}</span>}
  </span>;
}
